import type { AnalysisTask } from '@/api/generated/model'
import { taskScoreFor } from '@/components/evals/analysis'

export interface SectionTask {
  task: AnalysisTask
  score: number | null
}

export interface TaskSection {
  name: string
  tasks: SectionTask[]
  score: number | null
  scoredCount: number
}

function sectionName(sections: ReadonlyMap<string, string>, slug: string): string {
  return sections.get(slug)?.trim() || 'Uncategorized'
}

export function filterSectionTasks(tasks: AnalysisTask[], sections: ReadonlyMap<string, string>, query: string): AnalysisTask[] {
  const needle = query.trim().toLowerCase()
  return tasks.filter((task) =>
    `${sectionName(sections, task.slug)} ${task.display_name} ${task.slug}`.toLowerCase().includes(needle))
}

function rank(score: number | null): number {
  return score ?? -1
}

function summarize(name: string, tasks: SectionTask[]): TaskSection {
  const scored = tasks.flatMap((task) => task.score === null ? [] : [task.score])
  return {
    name,
    tasks: [...tasks].sort((a, b) => rank(b.score) - rank(a.score)
      || a.task.display_name.localeCompare(b.task.display_name)
      || a.task.slug.localeCompare(b.task.slug)),
    score: scored.length === 0 ? null : scored.reduce((sum, score) => sum + score, 0) / scored.length,
    scoredCount: scored.length,
  }
}

/** Section means weight scored tasks equally, matching the benchmark headline. */
export function groupTaskSections(
  tasks: AnalysisTask[],
  sections: ReadonlyMap<string, string>,
  model: string | null,
): TaskSection[] {
  const groups = new Map<string, SectionTask[]>()
  for (const task of tasks) {
    const name = sectionName(sections, task.slug)
    const entry = { task, score: taskScoreFor(task, model) }
    const group = groups.get(name)
    if (group === undefined) groups.set(name, [entry])
    else group.push(entry)
  }
  return [...groups].map(([name, entries]) => summarize(name, entries))
    .sort((a, b) => rank(b.score) - rank(a.score) || a.name.localeCompare(b.name))
}
